import React, { type ReactNode } from 'react';
import type { FieldValidator } from 'final-form';
import { Field } from 'react-final-form';
import { MenuItem, Stack, TextField, Typography } from '@cherre-frontend/ui';

interface ProviderSettingsFormFieldProps {
  icon?: ReactNode;
  label: string;
  name: string;
  subtitle: string;
  expand?: boolean;
  validator?: FieldValidator<string>;
  options?: { label: string; value: string | number }[];
  isOptionDisabled?: (value: string | number) => boolean;
}

export const ProviderSettingsFormField = ({
  label,
  name,
  subtitle,
  icon,
  expand,
  validator,
  options,
  isOptionDisabled,
}: ProviderSettingsFormFieldProps) => {
  const isSelect = !!options;
  const labelId = `${name}-label`;
  const inputId = `${name}-input`;
  const selectProps = {
    select: true,
    SelectProps: { labelId, displayEmpty: true },
    children: options?.map(({ label, value }) => {
      const isDisabled = isOptionDisabled?.(value);
      return (
        <MenuItem
          key={value}
          value={value}
          disabled={isDisabled}
          sx={{
            whiteSpace: 'normal',
            flexDirection: 'column',
            alignItems: 'flex-start',
          }}
        >
          {label}
          {isDisabled && (
            <Typography variant='body2' component='span'>
              Same value cannot be selected for both Thousands & Decimal
              formatting.
            </Typography>
          )}
        </MenuItem>
      );
    }),
  };

  return (
    <Stack
      gap={1}
      gridColumn={{ xs: undefined, md: expand ? 'span 2' : undefined }}
    >
      <Stack gap={0.5}>
        <Stack direction='row' gap={0.5} alignItems='center'>
          {icon}
          <Typography
            component='label'
            id={labelId}
            htmlFor={isSelect ? undefined : inputId}
            variant='body2'
            fontWeight='bold'
          >
            {label}
          </Typography>
        </Stack>

        <Typography variant='body2' fontWeight='regular'>
          {subtitle}
        </Typography>
      </Stack>

      <Field name={name} parse={(value) => value} validate={validator}>
        {({ input, meta: { touched, error } }) => (
          <TextField
            {...input}
            size='small'
            inputProps={{ id: inputId }}
            error={!!error}
            helperText={touched && error ? error : undefined}
            {...(options ? selectProps : {})}
          />
        )}
      </Field>
    </Stack>
  );
};
